/* Marketplace cart drawer (replaces Dawn cart-drawer.js). Keeps Dawn's product-form contract:
   renderContents(), getSectionsToRender(), setActiveElement(), open(), classList is-empty */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.body = this.querySelector('[data-body]');
    this.foot = this.querySelector('[data-foot]');
    this.protSlot = this.querySelector('[data-prot-slot]');
    this.checkoutBtn = this.querySelector('[data-checkout]');
    this.cfg = {
      tiersEnable: this.dataset.tiersEnable === 'true',
      tiers: (this.dataset.tiers || '').split(',').map(s => s.trim()).filter(Boolean).map(s => { const [q, p] = s.split(':'); return { qty: parseInt(q, 10), pct: parseInt(p, 10) }; }).filter(t => t.qty > 0 && t.pct > 0),
      dealsEnable: this.dataset.dealsEnable === 'true',
      dealsText: this.dataset.dealsText || "Today's prices hold for",
      protVariant: this.dataset.protVariant,
      protTitle: this.dataset.protTitle,
      protPrice: this.dataset.protPrice,
      protDefault: this.dataset.protDefault === 'true',
      upsEnable: this.dataset.upsEnable === 'true',
      upsTitle: this.dataset.upsTitle || 'Goes well with this',
      upsLimit: parseInt(this.dataset.upsLimit || '10', 10),
      currency: this.dataset.currency || 'USD',
      locale: this.dataset.locale || 'en'
    };
    this.cart = null; this.ups = null; this.upsFor = null;
    this.querySelectorAll('[data-close]').forEach(el => el.addEventListener('click', () => this.close()));
    this.addEventListener('keyup', e => { if (e.code === 'Escape') this.close(); });
    const link = document.querySelector('#cart-icon-bubble');
    if (link) { link.setAttribute('role', 'button'); link.addEventListener('click', e => { e.preventDefault(); this.open(link); }); }
    // bound on the host so the footer (upsell + protection) is covered too
    this.addEventListener('click', e => this.onClick(e));
    this.addEventListener('change', e => { const i = e.target.closest('[data-qty-input]'); if (i) this.change(i.dataset.key, parseInt(i.value, 10) || 0); });
    // own the add: the theme's own path refetches the whole product section and
    // only then hands us the cart, which is what made the drawer open empty
    document.addEventListener('submit', (e) => this.onAddSubmit(e), true);
    this.refresh();
    setInterval(() => this.tickCountdown(), 1000);
  }
  onAddSubmit(e) {
    const form = e.target && e.target.closest ? e.target.closest('form[action*="/cart/add"]') : null;
    if (!form || form.id === 'dn-installments-form') return;
    e.preventDefault(); e.stopImmediatePropagation();
    const btn = form.querySelector('[type="submit"]');
    if (btn) { btn.classList.add('loading'); btn.setAttribute('aria-disabled', 'true'); }
    this._addError = null;
    this._loading = true; this.render();
    this.open(undefined, true);
    fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form), credentials: 'same-origin' })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })).catch(() => ({ ok: r.ok, d: null })))
      .then((res) => {
        if (res.d && res.d.status) this._addError = res.d.description || res.d.message || null;
        else if (!res.ok) this._addError = 'We could not add that just now. Please try again.';
      })
      .catch(() => { this._addError = 'We could not add that just now. Please try again.'; })
      .then(() => this.refresh())
      .then(() => { if (btn) { btn.classList.remove('loading'); btn.removeAttribute('aria-disabled'); } });
  }
  /* ---- Dawn contract ---- */
  getSectionsToRender() { return []; }
  setActiveElement(el) { this.activeElement = el; }
  renderContents(parsedState) {
    this.classList.remove('is-empty');
    // the body still holds whatever was drawn on page load (often the empty
    // state), so repaint it as a placeholder before opening
    this._loading = true; this.render();
    this.open(undefined, true);
    this.refresh().then(() => {
      // something was just added, so an empty cart here means a stale read
      if (!this.realItems().length) {
        this._loading = true; this.render();
        setTimeout(() => this.refresh(), 350);
      }
    });
  }
  open(triggeredBy, skipRefresh) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('active'); document.body.classList.add('overflow-hidden', 'dn-cd-open');
    // the panel is on screen straight away; the fetch catches up behind it
    if (!skipRefresh) {
      if (!this.cart || !this.realItems().length) { this._loading = true; this.render(); }
      this.refresh();
    }
    setTimeout(() => { const c = this.querySelector('.dn-cd__close'); if (c) c.focus(); }, 300);
  }
  close() { this.classList.remove('active'); document.body.classList.remove('overflow-hidden', 'dn-cd-open'); if (this.activeElement && this.activeElement.focus) this.activeElement.focus(); }
  /* ---- data ---- */
  money(cents) { try { return new Intl.NumberFormat(this.cfg.locale, { style: 'currency', currency: this.cfg.currency }).format(cents / 100); } catch (e) { return '$' + (cents / 100).toFixed(2); } }
  async refresh() {
    try {
      // no-store + a cache buster: a cached empty /cart.js is what made the
      // drawer say "nothing in here" right after an add
      const r = await fetch('/cart.js?t=' + Date.now(), { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' });
      const data = await r.json();
      if (data && Array.isArray(data.items)) this.cart = data;
    } catch (e) {
      // a failed read must not leave the panel stuck on the placeholder
      if (!this.cart) this.cart = { items: [], item_count: 0, total_price: 0 };
    } finally { this._loading = false; }
    this.render(); this.updateBubble();
    if (this.cfg.upsEnable) this.loadUpsells();
    return this.cart;
  }
  /* ---- writes: paint first, talk to Shopify after ---- */
  localTotals() {
    this.cart.item_count = this.cart.items.reduce((n, i) => n + i.quantity, 0);
    this.cart.total_price = this.cart.items.reduce((n, i) => n + i.final_line_price, 0);
  }
  paint() { this.render(); this.updateBubble(); this.loadCompareAt(); }
  /* the cart API omits compare-at prices, so they are read once per product
     from its own JSON and kept for the life of the page */
  loadCompareAt() {
    if (!this.cart) return;
    this.cap = this.cap || {};
    this._capSeen = this._capSeen || {};
    const want = [];
    this.cart.items.forEach(i => { if (i.handle && !this._capSeen[i.handle]) { this._capSeen[i.handle] = 1; want.push(i.handle); } });
    if (!want.length) return;
    Promise.all(want.map(h => fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(p => { if (p && p.variants) p.variants.forEach(v => { this.cap[v.id] = v.compare_at_price || 0; }); })
      .catch(() => {})))
      .then(() => { if (this.cart) this.render(); });
  }
  post(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body), credentials: 'same-origin', keepalive: true });
  }
  change(key, qty) {
    let variant = null;
    // optimistic: the row moves the moment it is clicked
    if (this.cart) {
      const line = this.cart.items.find(i => i.key === key);
      if (line) {
        variant = String(line.variant_id);
        if (qty <= 0) {
          this.cart.items = this.cart.items.filter(i => i.key !== key);
        } else {
          const unit = line.final_line_price / line.quantity;
          const was = line.original_line_price / line.quantity;
          line.quantity = qty;
          line.final_line_price = Math.round(unit * qty);
          line.original_line_price = Math.round(was * qty);
        }
        this.localTotals();
        this.paint();
      }
    }
    // Shopify re-keys a line whenever its discount allocation changes, so the
    // key captured at render time can already be stale by the time the request
    // goes out. The variant is remembered alongside it and flush() re-resolves
    // the live line from a fresh cart before writing.
    this._queue = this._queue || {};
    this._queue[key] = { qty: qty, variant: variant };
    clearTimeout(this._flushT);
    this._flushT = setTimeout(() => this.flush(), 90);
    return Promise.resolve();
  }
  async flush() {
    const q = this._queue; this._queue = null;
    if (!q || this._sending) { if (q) { this._queue = q; clearTimeout(this._flushT); this._flushT = setTimeout(() => this.flush(), 80); } return; }
    this._sending = true;
    try {
      for (const key in q) {
        const want = q[key];
        // the fast path is one round trip on the key we already hold
        const r = await this.post('/cart/change.js', { id: key, quantity: want.qty });
        if (r.ok) { this.cart = await r.json(); continue; }
        // Shopify re-keys a line when its discount allocation changes, so a key
        // from an earlier render can be rejected; re-resolve by position, which
        // costs an extra trip only in that case
        try {
          const c = await fetch('/cart.js?t=' + Date.now(), { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' }).then(x => x.json());
          if (!c || !Array.isArray(c.items)) continue;
          this.cart = c;
          let idx = c.items.findIndex(i => i.key === key);
          if (idx < 0 && want.variant) idx = c.items.findIndex(i => String(i.variant_id) === want.variant);
          if (idx < 0) continue;               // already gone: nothing to send
          const r2 = await this.post('/cart/change.js', { line: idx + 1, quantity: want.qty });
          if (r2.ok) this.cart = await r2.json();
        } catch (err) {}
      }
    } catch (e) {}
    this._sending = false;
    this.paint();
    if (this.cfg.upsEnable) this.loadUpsells();
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: this.cart });
    if (this._queue) this.flush();
  }
  async add(id, qty) {
    const n = qty || 1;
    // one round trip instead of two: update.js answers with the whole cart
    if (this.cart) {
      let have = 0;
      this.cart.items.forEach(i => { if (String(i.variant_id) === String(id)) have += i.quantity; });
      try {
        const r = await this.post('/cart/update.js', { updates: { [id]: have + n } });
        if (r.ok) {
          this.cart = await r.json();
          this.paint();
          if (this.cfg.upsEnable) this.loadUpsells();
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: this.cart });
          return this.cart;
        }
      } catch (e) {}
    }
    try { await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: id, quantity: n }] }) }); } catch (e) {}
    return this.refresh();
  }
  updateBubble() {
    const b = document.querySelector('#cart-icon-bubble'); if (!b) return;
    let bub = b.querySelector('.cart-count-bubble'); const n = this.cart ? this.cart.item_count : 0;
    // the count stays in place at 0 (the phone header shows it, as Loftim does);
    // wide screens hide an empty one through .is-zero
    if (!bub) { bub = document.createElement('div'); bub.className = 'cart-count-bubble'; b.appendChild(bub); }
    bub.innerHTML = '<span aria-hidden="true">' + n + '</span>';
    bub.classList.toggle('is-zero', n === 0);
    this.classList.toggle('is-empty', n === 0);
  }
  onClick(e) {
    // overlay and X: bound here because the constructor may run before the
    // element's children have been parsed
    if (e.target.closest('[data-close]')) { e.preventDefault(); return this.close(); }
    const inc = e.target.closest('[data-inc]'); if (inc) { const i = inc.parentElement.querySelector('input'); return this.change(i.dataset.key, (parseInt(i.value, 10) || 0) + 1); }
    const dec = e.target.closest('[data-dec]'); if (dec) { const i = dec.parentElement.querySelector('input'); return this.change(i.dataset.key, Math.max(0, (parseInt(i.value, 10) || 0) - 1)); }
    const rm = e.target.closest('[data-remove]'); if (rm) return this.change(rm.dataset.remove, 0);
    const pt = e.target.closest('[data-prot-toggle]'); if (pt) { const line = this.protLine(); if (line) { try { sessionStorage.setItem('dnProtOff', '1'); } catch (err) {} return this.change(line.key, 0); } try { sessionStorage.removeItem('dnProtOff'); } catch (err) {} return this.add(this.cfg.protVariant, 1); }
    const ua = e.target.closest('[data-ups-add]'); if (ua) { const card = ua.closest('[data-ups-card]'); const sel = card.querySelector('select'); return this.add(sel ? sel.value : ua.dataset.upsAdd, 1); }
    const nv = e.target.closest('[data-ups-nav]'); if (nv) { const t = this.querySelector('.dn-ups__track'); if (t) t.scrollBy({ left: (nv.dataset.upsNav === 'next' ? 1 : -1) * t.clientWidth, behavior: 'smooth' }); }
  }
  protLine() { if (!this.cfg.protVariant || !this.cart) return null; return this.cart.items.find(i => String(i.variant_id) === String(this.cfg.protVariant)) || null; }
  realItems() { return (this.cart ? this.cart.items : []).filter(i => String(i.variant_id) !== String(this.cfg.protVariant)); }
  /* ---- render ---- */
  render() {
    if (!this.cart) return;
    const items = this.realItems(); const prot = this.protLine();
    const qty = items.reduce((s, i) => s + i.quantity, 0);
    let h = '';
    if (!items.length) {
      // a fetch is on its way: show a placeholder row, not "nothing in here"
      if (this._loading) {
        this.body.innerHTML = '<div class="dn-cd__skel"><span></span><div><i></i><i></i><i></i></div></div>';
        this.foot.style.display = 'none';
        return;
      }
      if (this._addError) h += '<p class="dn-cd__err">' + this.esc(this._addError) + '</p>';
      h += '<div class="dn-cd__empty"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"><path d="M6 8h12l-1 12H7L6 8Z"/><path d="M9 10V7a3 3 0 0 1 6 0v3" stroke-linecap="round"/></svg><h3>Nothing in here yet</h3><a class="dn-ups__add" style="display:inline-flex;align-items:center;border-radius:2.4rem;text-decoration:none" href="/collections/all">Start browsing</a></div>';
      this.body.innerHTML = h; this.foot.style.display = 'none';
      if (prot) this.change(prot.key, 0);
      return;
    }
    this.foot.style.display = '';
    // never re-add the cover while a removal is still on its way out, or the
    // two requests race and the line the shopper asked to delete comes back
    if (this.cfg.protVariant && this.cfg.protDefault && !prot && !this._protAuto && !this._queue && !this._sending) { let off = false; try { off = !!sessionStorage.getItem('dnProtOff'); } catch (err) {} if (!off) { this._protAuto = true; this.add(this.cfg.protVariant, 1).then(() => { this._protAuto = false; }); return; } }
    if (this.cfg.dealsEnable) h += '<div class="dn-cd__deals"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5M9 2h6"/></svg><span>' + this.esc(this.cfg.dealsText) + ' <b data-deals-cd>&nbsp;</b></span></div>';
    if (this._addError) h += '<p class="dn-cd__err">' + this.esc(this._addError) + '</p>';
    // the cover is a service line, not one of the items: the discounts are
    // scoped to a collection that excludes it, so the ladder counts products
    if (this.cfg.tiersEnable && this.cfg.tiers.length) h += this.renderTiers(qty);
    h += '<ul class="dn-cd__items">' + items.map(i => this.renderItem(i)).join('') + '</ul>';
    h += '<div class="dn-ups" data-ups-wrap style="display:none"></div>';
    // the protection row lives in the sticky footer, right above Checkout,
    // so it stays in view on every screen size instead of scrolling away
    if (this.protSlot) this.protSlot.innerHTML = this.cfg.protVariant ? '<div class="dn-prot"><div class="dn-prot__icon"><svg class="dn-prot__icon-svg" viewBox="0 0 64 64" aria-hidden="true"><defs><linearGradient id="dnPrBg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#22549f" stop-opacity=".10"/><stop offset="1" stop-color="#22549f" stop-opacity=".22"/></linearGradient><linearGradient id="dnPrSh" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#22549f" stop-opacity=".78"/><stop offset="1" stop-color="#22549f"/></linearGradient></defs><rect x="2" y="2" width="60" height="60" rx="16" fill="url(#dnPrBg)"/><path d="M32 11 49 17.5v12.3c0 11-7.2 19.6-17 22.7-9.8-3.1-17-11.7-17-22.7V17.5z" fill="url(#dnPrSh)"/><path d="M32 11v41.5c-9.8-3.1-17-11.7-17-22.7V17.5z" fill="#fff" opacity=".12"/><path d="m24.5 31.5 5.2 5.2 10-10.4" fill="none" stroke="#fff" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round"/></svg></div><div class="dn-prot__body"><div class="dn-prot__row"><p class="dn-prot__t">' + this.esc(this.cfg.protTitle) + '</p><span class="dn-prot__price">' + this.cfg.protPrice + '</span></div><p class="dn-prot__d">Covers your parcel if it is damaged, lost or stolen on the way.</p></div><button type="button" class="dn-switch' + (prot ? ' on' : '') + '" data-prot-toggle aria-pressed="' + (prot ? 'true' : 'false') + '" aria-label="' + this.esc(this.cfg.protTitle) + '"></button></div>' : '';
    this.body.innerHTML = h;
    this.checkoutBtn.textContent = 'Go to checkout • ' + this.money(this.cart.total_price);
    this.tickCountdown();
    if (this.ups && this.upsFor) this.renderUpsells();
  }
  renderItem(i) {
    const img = i.featured_image && i.featured_image.url ? i.featured_image.url.replace(/(\.[a-z]+)(\?|$)/, '_200x200$1$2') : '';
    const varText = i.variant_title && i.variant_title !== 'Default Title' ? '<p class="dn-cd__item-var">' + this.esc(i.variant_title) + '</p>' : '';
    // /cart.js carries no compare-at price, so it comes from the product JSON
    // that loadCompareAt() caches; a line discount is the fallback crossing-out
    const cap = (this.cap && this.cap[i.variant_id]) || 0;
    let was = '';
    if (cap > i.final_price) was = '<s>' + this.money(cap * i.quantity) + '</s>';
    else if (i.original_line_price > i.final_line_price) was = '<s>' + this.money(i.original_line_price) + '</s>';
    // the discount Shopify applied to this line, named as checkout names it
    const discText = (i.line_level_discount_allocations || []).filter(a => a.amount > 0).map(a =>
      '<p class="dn-cd__item-disc">' + '<svg class="cd-disc__ic" viewBox="0 -960 1087 1024" aria-hidden="true" focusable="false"><path transform="scale(1,-1)" fill="currentColor" d="M924 525V878Q924 912 901.5 936.0Q879 960 852 960H517Q496 960 482.5 953.0Q469 946 462 933L27 480Q7 453 7.0 421.0Q7 389 27 362L362 8Q383 -19 417.0 -19.0Q451 -19 471 8L906 462Q913 475 918.5 493.0Q924 511 924 525ZM707 670Q680 670 661.5 688.0Q643 706 643 733Q643 761 661.5 779.0Q680 797 707 797Q734 797 752.0 779.0Q770 761 770 733Q770 706 752.0 688.0Q734 670 707 670ZM1006 878V489Q1006 475 1000.5 461.5Q995 448 988 434L544 -28L553 -37Q573 -64 607.0 -64.0Q641 -64 662 -37L1060 380Q1074 394 1080.5 408.5Q1087 423 1087 443V797Q1087 831 1063.5 854.5Q1040 878 1006 878Z"/></svg>' + '<span>' + this.esc(a.discount_application.title) + '&nbsp;(-' + this.money(a.amount) + ')</span></p>').join('');
    return '<li class="dn-cd__item"><a href="' + i.url + '">' + (img ? '<img src="' + img + '" alt="" width="84" height="84" loading="lazy">' : '') + '</a><div><a class="dn-cd__item-title" href="' + i.url + '">' + this.esc(i.product_title) + '</a>' + varText + discText +
      '<div class="dn-cd__ctrl"><span class="dn-qty"><button type="button" data-dec aria-label="Decrease">&minus;</button><input type="number" min="0" value="' + i.quantity + '" data-qty-input data-key="' + i.key + '" aria-label="Quantity"><button type="button" data-inc aria-label="Increase">+</button></span><button type="button" class="dn-cd__remove" data-remove="' + i.key + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/></svg><span class="visually-hidden">Remove</span></button></div></div>' +
      '<div class="dn-cd__item-price">' + was + this.money(i.final_line_price) + '</div></li>';
  }
  renderTiers(qty) {
    const tiers = this.cfg.tiers.slice().sort((a, b) => a.qty - b.qty); const max = tiers[tiers.length - 1].qty;
    const next = tiers.find(t => qty < t.qty); const cur = tiers.filter(t => qty >= t.qty).pop();
    let msg = (cur ? '<strong>' + cur.pct + '% off is on</strong> \u00b7 ' : '') + (next ? '<strong>' + (next.qty - qty) + ' more item' + (next.qty - qty > 1 ? 's' : '') + '</strong> takes <strong>' + next.pct + '% off</strong>' : 'Biggest saving reached on this order');
    const n = tiers.length; const pos = i => ((i + 1) / n) * 100; let pct = 0; if (next) { const ni = tiers.indexOf(next); const prevQ = ni > 0 ? tiers[ni - 1].qty : 0; const base = ni > 0 ? pos(ni - 1) : 0; pct = base + (qty - prevQ) / (next.qty - prevQ) * (pos(ni) - base); } else pct = 100;
    const dots = tiers.map((t, i) => '<span class="dn-tiers__dot' + (qty >= t.qty ? ' is-on' : '') + '" style="left:' + pos(i) + '%"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1"/></svg></span>').join('');
    const labels = tiers.map((t, i) => '<span class="' + (qty >= t.qty ? 'is-on' : '') + '" style="left:' + pos(i) + '%">' + t.qty + ' items<b>' + t.pct + '% OFF</b></span>').join('');
    return '<div class="dn-tiers"><p class="dn-tiers__msg">' + msg + '</p><div class="dn-tiers__track"><div class="dn-tiers__fill" style="width:' + pct + '%">&nbsp;</div>' + dots + '</div><div class="dn-tiers__labels">' + labels + '</div></div>';
  }
  tickCountdown() {
    const el = this.querySelector('[data-deals-cd]'); if (!el) return;
    const now = new Date(); const end = new Date(now); end.setHours(24, 0, 0, 0);
    let s = Math.max(0, Math.floor((end - now) / 1000)); const hh = Math.floor(s / 3600); const mm = Math.floor((s % 3600) / 60); const ss = s % 60;
    el.textContent = hh + 'h ' + String(mm).padStart(2, '0') + 'm ' + String(ss).padStart(2, '0') + 's';
  }
  async loadUpsells() {
    const items = this.realItems(); if (!items.length) return;
    const pid = items[0].product_id; if (this.upsFor === pid && this.ups) { this.renderUpsells(); return; }
    try {
      let r = await fetch('/recommendations/products.json?product_id=' + pid + '&limit=' + this.cfg.upsLimit + '&intent=related'); let d = await r.json(); let prods = d.products || [];
      if (prods.length < 4) { r = await fetch('/collections/all/products.json?limit=' + this.cfg.upsLimit); d = await r.json(); prods = prods.concat(d.products || []); }
      const inCart = new Set(this.cart.items.map(i => i.product_id));
      this.ups = prods.filter(p => !inCart.has(p.id) && p.available !== false && String(p.variants[0].id) !== String(this.cfg.protVariant)).slice(0, this.cfg.upsLimit); this.upsFor = pid; this.renderUpsells();
    } catch (e) {}
  }
  renderUpsells() {
    const wrap = this.querySelector('[data-ups-wrap]'); if (!wrap) return; if (!this.ups || !this.ups.length) { wrap.style.display = 'none'; wrap.innerHTML = ''; return; }
    const cards = this.ups.map(p => {
      const img = (p.featured_image || (p.images && p.images[0]) || ''); const src = typeof img === 'string' ? img : (img.src || '');
      const v0 = p.variants[0]; const cents = x => (typeof x === 'number' ? x : Math.round(parseFloat(x) * 100)); const price = this.money(cents(v0.price)); const cmp = v0.compare_at_price && cents(v0.compare_at_price) > cents(v0.price) ? '<s>' + this.money(cents(v0.compare_at_price)) + '</s>' : '';
      const sel = p.variants.length > 1 ? '<select aria-label="Options">' + p.variants.filter(v => v.available !== false).map(v => '<option value="' + v.id + '">' + this.esc(v.title) + '</option>').join('') + '</select>' : '';
      return '<div class="dn-ups__card" data-ups-card><a href="/products/' + p.handle + '">' + (src ? '<img src="' + src.replace(/(\.[a-z]+)(\?|$)/, '_200x200$1$2') + '" alt="" loading="lazy">' : '') + '</a><div><p class="dn-ups__t" title="' + this.esc(p.title) + '">' + this.esc(p.title) + '</p><p class="dn-ups__p">' + cmp + price + '</p>' + sel + '<button type="button" class="dn-ups__add" data-ups-add="' + v0.id + '">Add</button></div></div>';
    }).join('');
    wrap.style.display = ''; wrap.innerHTML = '<div class="dn-ups__head"><h4>' + this.esc(this.cfg.upsTitle) + '</h4><div class="dn-ups__nav"><button type="button" data-ups-nav="prev" aria-label="Previous">&lsaquo;</button><button type="button" data-ups-nav="next" aria-label="Next">&rsaquo;</button></div></div><div class="dn-ups__track">' + cards + '</div>';
  }
  esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]); }
}
customElements.define('cart-drawer', CartDrawer);
/* Dawn's cart page items component is not used by the marketplace cart template; keep a no-op for safety */
if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', class extends HTMLElement {});
